/**
 * The reading measure every block on a form page shares — question text, question
 * body, text blocks, the grading table and the answer inputs. Keeping it in one
 * place is what makes their left and right edges line up; when each component
 * carried its own number they drifted (700 vs 640) and the page looked ragged.
 *
 * 780 rather than a tighter 640: at the 15px body size that is roughly 80
 * characters a line — still inside the comfortable range for prose, while filling
 * enough of the page that a workbook section doesn't read as a narrow ribbon down
 * the middle of a very wide card.
 */
export const FORM_CONTENT_MAX_WIDTH = 780;

/**
 * The form body itself. Sits a little wider than the reading measure so the answer
 * cards and callouts have room to breathe either side of the text they follow.
 */
export const FORM_BODY_MAX_WIDTH = 880;

/**
 * Styling for admin-authored rich HTML inside a form — tables and embedded media
 * (images, Vimeo iframes) written in the dashboard's rich text editor.
 *
 * Kept in one place so the question text, the question body and the workbook text
 * blocks all present authored content identically.
 */
export const formRichContentSx = {
  // `global.css` resets `ul { list-style-type: none }` app-wide, so an authored
  // bullet list renders with no markers at all. Restoring the marker on the `li`
  // (rather than the `ul`) puts it back without colliding with the padding and
  // alignment each caller already sets on `& ul, & ol`.
  '& ul > li': { listStyleType: 'disc' },
  '& ol > li': { listStyleType: 'decimal' },
  '& li': { marginBottom: '0.3em' },
  '& li:last-child': { marginBottom: 0 },
  // `table-layout: auto` on purpose, where the editor uses `fixed`: the editor
  // needs fixed for its column resizing to mean anything, but here the column
  // widths that resizing wrote are only hints — auto lets the browser fit a wide
  // table to the reading measure instead of pushing the page sideways. Auto only
  // holds while there is width to distribute, which is why the phone case below
  // stops fitting and starts scrolling.
  '& table': {
    width: '100%',
    tableLayout: 'auto',
    borderCollapse: 'collapse',
    margin: '12px 0',
    fontSize: '0.9375rem',
    // On a phone an authored table has more columns than the viewport can hold,
    // and `auto` layout answers that by squeezing every column down to one word
    // a line — unreadable, and the rows grow tall enough to lose their shape.
    // `display: block` makes the table its own horizontal scroll container (the
    // rows still lay out against one anonymous table box, so the columns stay
    // aligned), so cells keep a readable width and the page never scrolls
    // sideways with them.
    display: { xs: 'block', sm: 'table' },
    overflowX: { xs: 'auto', sm: 'visible' },
    // Swiping the table must not hand the gesture to the browser's back-swipe.
    overscrollBehaviorX: 'contain',
    // The scrollbar is the only affordance saying there is more to the right, so
    // it is drawn rather than left to the platform default.
    '&::-webkit-scrollbar': { height: 6 },
    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
    '&::-webkit-scrollbar-thumb': { borderRadius: 3, backgroundColor: 'grey.300' },
  },
  '& th, & td': {
    border: '1px solid',
    borderColor: 'divider',
    padding: '8px 10px',
    verticalAlign: 'top',
    textAlign: 'left',
    // Wide enough that a cell wraps into phrases rather than single words; this
    // is what pushes the table past the viewport and makes it scroll.
    minWidth: { xs: 140, sm: 0 },
    '& > *:last-child': { marginBottom: 0 },
  },
  '& th': { backgroundColor: 'action.hover', fontWeight: 700 },
  // Video embeds carry no usable intrinsic size (an iframe defaults to 300x150),
  // so they are given the 16:9 box the player expects.
  '& iframe': {
    display: 'block',
    width: '100%',
    aspectRatio: '16 / 9',
    height: 'auto',
    border: 0,
    borderRadius: '10px',
    margin: '12px 0',
  },
  '& img': { maxWidth: '100%', height: 'auto', borderRadius: '10px' },
  '& video': { maxWidth: '100%' },
} as const;
